import React from 'react';
import { styled } from '@compiled/react';
import LoadingButton from '@atlaskit/button/loading-button';
import { Field, ErrorMessage } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Anchor, Box, xcss } from '@atlaskit/primitives';
// eslint-disable-next-line import/order
import { RadioGroup } from '@atlaskit/radio';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { FormattedMessage } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';
import { PORTAL_RADIO_OPTIONS } from './types';

type Props = {
	canAgentsManageHelpCenterAnnouncement: boolean;
	canAdministerJira: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formProps: any;
	submitting: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	state: any;
	portalShortLink: string;
	projectId: number;
	dirty: boolean;
	intl: IntlShape;
};

const JIRA_SUPPORT_LINK =
	'https://support.atlassian.com/jira-service-management-cloud/docs/add-an-announcement-to-the-portal/#Add-an-announcement-to-a-help-center';

export const Announcements = (props: Props) => {
	const {
		intl: { formatMessage },
		canAgentsManageHelpCenterAnnouncement,
		canAdministerJira,
		portalShortLink,
		projectId,
		submitting,
		state,
		formProps,
		dirty,
	} = props;

	const agentsAndAdminsOption = {
		name: 'portal-announcements',
		value: PORTAL_RADIO_OPTIONS.AGENTS_AND_ADMINS,
		label: formatMessage(messages.agentsCanAdd),
	};

	const onlyAdminsOption = {
		name: 'portal-announcements',
		value: PORTAL_RADIO_OPTIONS.ONLY_ADMINS,
		label: formatMessage(messages.onlyAdminsCanAddJsm),
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const options: any /* OptionsPropType(@atlaskit/radio/types) */ = [
		agentsAndAdminsOption,
		onlyAdminsOption,
	];

	const helpCenterAnnouncementDetails = canAgentsManageHelpCenterAnnouncement
		? messages.agentsCanChange
		: messages.agentsCannotChange;

	const helpCenterAnnouncementDetailsNotAdmin = canAgentsManageHelpCenterAnnouncement
		? messages.agentsCanChangeNotAdmin
		: messages.agentsCannotChangeNotAdmin;

	const announcementPageDescription = () =>
		canAdministerJira ? (
			<>
				{formatMessage(helpCenterAnnouncementDetails)}
				<FormattedMessage
					{...messages.jsdConfigLink}
					values={{
						configLink: (
							<Anchor href="/jira/settings/products/jira-service-management-configuration">
								{formatMessage(messages.jiraServiceManagementConfigurationPage)}
							</Anchor>
						),
					}}
				/>
			</>
		) : (
			<>{formatMessage(helpCenterAnnouncementDetailsNotAdmin)}</>
		);

	const announcementPageDescriptionMultiHc = () => {
		if (canAdministerJira && !canAgentsManageHelpCenterAnnouncement) {
			return (
				<FormattedMessage
					{...messages.agentsCannotChangeMultiHc}
					values={{
						a: (chunks) => (
							<Anchor
								href="/jira/settings/products/jira-service-management-configuration"
								target="_blank"
							>
								{chunks}
							</Anchor>
						),
					}}
				/>
			);
		}

		if (canAdministerJira && canAgentsManageHelpCenterAnnouncement) {
			return (
				<>
					<p>
						<FormattedMessage
							{...messages.agentsCanChangeMultiHc}
							values={{
								a: (chunks) => (
									<Anchor
										href="/jira/settings/products/jira-service-management-configuration"
										target="_blank"
									>
										{chunks}
									</Anchor>
								),
							}}
						/>
					</p>
					<p>
						<FormattedMessage
							{...messages.agentsCanChangeFindMoreMultiHc}
							values={{
								a: (chunks) => (
									<Anchor href={JIRA_SUPPORT_LINK} target="_blank">
										{chunks}
									</Anchor>
								),
							}}
						/>
					</p>
				</>
			);
		}

		return <>{formatMessage(helpCenterAnnouncementDetailsNotAdmin)}</>;
	};

	return (
		<>
			<p>
				<Anchor href={portalShortLink} target="_blank">
					{formatMessage(messages.managePortalAnnouncements)}
				</Anchor>
			</p>
			<form {...formProps} name="radioForm">
				<Field
					name="radioField"
					defaultValue={state.defaultOption}
					label={formatMessage(messages.canAgentsAddAnnouncementsJsm)}
				>
					{({ fieldProps }) => (
						<>
							{}
							<RadioGroup options={options} {...fieldProps} />
							{state.formFailedToSubmit && (
								<ErrorMessage>{formatMessage(messages.errorFailed)}</ErrorMessage>
							)}
						</>
					)}
				</Field>
				<Box xcss={buttonContainerStyles}>
					<LoadingButton
						type="submit"
						appearance="primary"
						isLoading={state.isLoading}
						isDisabled={!dirty || submitting}
						onClick={(event, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'customerPortalRadioSubmit', {
								projectId,
							});
						}}
					>
						{formatMessage(messages.save)}
					</LoadingButton>
				</Box>
			</form>
			<Box xcss={headingContainerStyles}>
				<Heading level="h200">{formatMessage(messages.helpCentreAnnouncement)}</Heading>
			</Box>
			<PageDescription>
				{ff('multi_help_center_kosh_integration')
					? announcementPageDescriptionMultiHc()
					: announcementPageDescription()}
			</PageDescription>
		</>
	);
};

export default injectIntl(Announcements);

const buttonContainerStyles = xcss({
	paddingTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageDescription = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 65}px`,
});

const headingContainerStyles = xcss({
	marginTop: 'space.250',
});
